/* =========================================================
   AURMANS — aurmans-motion.js
   Scroll reveals, header scroll states and product-card hover
   images. Dependency-free; pairs with aurmans-motion.css.
   Content is visible by default: this file only hides elements
   that start below the fold, right before observing them.
   ========================================================= */
(function () {
  'use strict';

  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }
  var inThemeEditor = !!(window.Shopify && window.Shopify.designMode);

  /* ---------- Scroll reveal (IntersectionObserver) ---------- */
  var REVEAL = [
    '.section-head',
    '.product-grid > .product-card',
    '.moments .kicker',
    '.moments .h2',
    '.moments .moment',
    '.showcase__media',
    '.showcase__content > *',
    '.split__content > *',
    '.split__media',
    '.ugc-tile',
    '.cta__inner > *'
  ].join(',');

  function initReveal() {
    if (inThemeEditor || reducedMotion() || !('IntersectionObserver' in window)) return;
    var main = qs('#MainContent');
    if (!main) return;

    // Read every position first, then write: one layout pass, and anything already on
    // screen (or above it, e.g. a restored scroll position) is never hidden.
    var fold = window.innerHeight * 0.92;
    var items = qsa(REVEAL, main).filter(function (el) {
      return el.getBoundingClientRect().top > fold;
    });
    if (!items.length) return;

    function reveal(el, delay) {
      el.style.setProperty('--aur-delay', delay + 'ms');
      el.classList.remove('is-pending');
      // Drop the helper class once done so the element's own transitions apply again.
      setTimeout(function () {
        el.classList.remove('aur-reveal');
        el.style.removeProperty('--aur-delay');
      }, delay + 1200);
    }

    var io = new IntersectionObserver(function (entries) {
      var step = 0;
      var furthest = -1;
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        if (entry.target === footer) { furthest = items.length; return; }
        io.unobserve(entry.target);
        reveal(entry.target, Math.min(step, 5) * 80);
        furthest = Math.max(furthest, items.indexOf(entry.target));
        step += 1;
      });
      // A fast jump (End key, anchor link) can skip an element without it ever intersecting.
      // Anything earlier in the page that is now above the viewport is shown at once.
      for (var i = 0; i < furthest; i++) {
        var el = items[i];
        if (el.classList.contains('is-pending') && el.getBoundingClientRect().bottom < 0) {
          io.unobserve(el);
          reveal(el, 0);
        }
      }
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0 });

    items.forEach(function (el) {
      el.classList.add('aur-reveal', 'is-pending');
      io.observe(el);
    });
    var footer = qs('#shopify-section-footer');
    if (footer) io.observe(footer);

    window.addEventListener('beforeprint', function () {
      qsa('.aur-reveal.is-pending').forEach(function (el) { reveal(el, 0); });
    });
  }

  /* ---------- Header: compact state + hide on scroll down, show on scroll up ---------- */
  function initHeaderScroll() {
    var section = qs('#shopify-section-header');
    if (!section || inThemeEditor) return;
    var mobileMenu = qs('#mobile-menu');
    var lastY = window.pageYOffset;
    var ticking = false;

    function canHide() {
      if (mobileMenu && !mobileMenu.hidden) return false;
      if (qs('.cats-trigger.is-open', section)) return false;
      return !section.contains(document.activeElement);
    }

    function update() {
      ticking = false;
      var y = window.pageYOffset;
      section.classList.toggle('is-scrolled', y > 40);
      if (y <= 320) {
        section.classList.remove('is-hidden');
      } else if (y > lastY + 6) {
        if (canHide()) section.classList.add('is-hidden');
      } else if (y < lastY - 6) {
        section.classList.remove('is-hidden');
      } else {
        return; // ignore tiny jitters, keep the reference point
      }
      lastY = y;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
    }, { passive: true });
    section.addEventListener('focusin', function () { section.classList.remove('is-hidden'); });
    update();
  }

  /* ---------- Product cards: second photo on hover, loaded only on first hover ---------- */
  function initCardHoverImage() {
    if (!window.matchMedia || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    // Delegated, so cards injected later (product recommendations) work too.
    document.addEventListener('pointerover', function (e) {
      var card = e.target.closest && e.target.closest('.product-card');
      if (!card || card.hasAttribute('data-alt-loading')) return;
      var img = qs('[data-hover-src]', card);
      if (!img) return;
      card.setAttribute('data-alt-loading', '');
      img.addEventListener('load', function () { card.classList.add('has-alt-image'); }, { once: true });
      if (img.getAttribute('data-hover-srcset')) img.srcset = img.getAttribute('data-hover-srcset');
      img.src = img.getAttribute('data-hover-src');
    });
  }

  function init() {
    initReveal();
    initHeaderScroll();
    initCardHoverImage();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
